/* Category feeds shared across carousel instances and short same-tab returns. */
(() => {
  'use strict';
  if (window.CsgCatalogueCache) return;

  const version = 1;
  const ttl = 180000;
  const storageKey = 'csg-catalogue-cache:v' + version;
  const maxEntries = 12;
  // sessionStorage accounts for UTF-16 strings; this is a conservative 1 MiB cap.
  const maxBytes = 1024 * 1024;
  const entries = new Map();
  const inflight = new Map();
  const staleKeys = new Set();
  const families = new Set(['hardtail', 'e_fully', 'gravel', 'e_hardtail', 'e_trekking', 'road', 'kids', 'youth', 'fully', 'trekking', 'e_gravel', 'e_city', 'cargo']);
  function validFamily(family, mode) {
    return families.has(family) || (mode === 'sale' && family === 'all');
  }
  function matchesFamily(item, family) {
    return item && typeof item === 'object' && (family === 'all' ? families.has(item.family) : item.family === family);
  }

  function markStale(key) {
    staleKeys.delete(key);
    staleKeys.add(key);
    if (staleKeys.size > 48) staleKeys.delete(staleKeys.values().next().value);
  }
  function fresh(entry, now = Date.now()) {
    return entry && Number.isFinite(entry.data.fetchedAt) && entry.data.fetchedAt <= now && now - entry.data.fetchedAt < ttl;
  }
  function prune() {
    const now = Date.now();
    for (const [key, entry] of entries) {
      if (!fresh(entry, now)) { entries.delete(key); markStale(key); }
    }
    let bytes = 0;
    for (const entry of entries.values()) bytes += entry.bytes;
    while (entries.size > maxEntries || bytes > maxBytes) {
      const oldest = entries.keys().next().value;
      bytes -= entries.get(oldest).bytes;
      entries.delete(oldest);
    }
  }
  function frozenData(data) {
    return Object.freeze({ items: Object.freeze(data.items), fetchedAt: data.fetchedAt, family: data.family });
  }
  function persist() {
    prune();
    try {
      const records = Array.from(entries, ([key, entry]) => ({ key, data: entry.data }));
      sessionStorage.setItem(storageKey, JSON.stringify({ version, records }));
    } catch { /* Private browsing, full storage, and blocked storage are supported. */ }
  }
  function remember(key, data) {
    const bytes = JSON.stringify({ key, data }).length * 2 + 64;
    entries.delete(key);
    staleKeys.delete(key);
    if (bytes <= maxBytes) entries.set(key, { data, bytes });
    persist();
  }
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (stored?.version === version && Array.isArray(stored.records)) {
      for (const record of stored.records.slice(-maxEntries)) {
        const data = record?.data;
        if (typeof record?.key !== 'string' || !data || !Array.isArray(data.items) || !Number.isFinite(data.fetchedAt)) continue;
        let identity;
        try { identity = JSON.parse(record.key); } catch { continue; }
        if (!Array.isArray(identity) || !validFamily(data.family, identity[6]) || identity[7] !== data.family) continue;
        if (!data.items.every(item => matchesFamily(item, data.family))) continue;
        const bytes = JSON.stringify(record).length * 2 + 64;
        if (bytes <= maxBytes) entries.set(record.key, { data: frozenData(data), bytes });
      }
      prune();
    }
  } catch { /* An absent or corrupt prior session entry is just a cache miss. */ }

  function requestScope(source, options = {}) {
    if (!validFamily(options.family, options.mode)) throw new Error('Unsupported catalogue family');
    if (options.mode !== 'my27' && options.mode !== 'sale') throw new Error('Unsupported catalogue mode');
    if (typeof options.selector !== 'string' || !options.selector) throw new Error('Missing catalogue selector');
    const url = new URL(source, location.href);
    if (url.origin !== location.origin || url.username || url.password) throw new Error('Catalogue URL must be same-origin');
    const section = url.searchParams.get('section_id');
    if (!section) throw new Error('Catalogue section_id is required');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.hash = '';
    url.searchParams.sort();
    const locale = options.locale || document.documentElement.lang || '';
    const currency = options.currency || window.Shopify?.currency?.active || '';
    const key = JSON.stringify([version, url.origin, locale, currency, url.searchParams.get('preview_theme_id') || '', window.Shopify?.theme?.id || '', options.mode, options.family, options.selector, url.href]);
    return { url, section, key, family: options.family, mode: options.mode, selector: options.selector };
  }
  function peek(source, options) {
    try {
      const { key } = requestScope(source, options);
      prune();
      const entry = entries.get(key);
      if (!entry) return undefined;
      // Touch the LRU order without extending the price-data lifetime.
      entries.delete(key); entries.set(key, entry);
      return entry.data;
    } catch { return undefined; }
  }
  function nextURL(value, current, scope) {
    const next = new URL(value, current);
    if (next.origin !== scope.url.origin || next.pathname !== scope.url.pathname || next.username || next.password) throw new Error('Invalid catalogue pagination URL');
    // Keep collection filters, locale/preview context, and the family endpoint.
    // Shopify may use either page or page_<hash> as its pagination parameter.
    const stableKeys = new Set(scope.url.searchParams.keys());
    for (const key of stableKeys) {
      if (key === 'page' || key.startsWith('page_')) continue;
      next.searchParams.delete(key);
      for (const value of scope.url.searchParams.getAll(key)) next.searchParams.append(key, value);
    }
    next.searchParams.set('section_id', scope.section);
    next.hash = '';
    next.searchParams.sort();
    return next;
  }
  async function load(scope, revalidate) {
    const items = [], seen = new Set();
    let url = scope.url;
    for (let page = 0; url && page < 100; page++) {
      if (seen.has(url.href)) throw new Error('Repeated catalogue page');
      seen.add(url.href);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      let html;
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', cache: revalidate ? 'no-cache' : 'default', signal: controller.signal });
        if (!response.ok) throw new Error('Catalogue feed ' + response.status);
        html = await response.text();
      } finally { clearTimeout(timeout); }
      const document = new DOMParser().parseFromString(html, 'text/html');
      const node = document.querySelector(scope.selector);
      if (!node) throw new Error('Catalogue feed missing');
      const data = JSON.parse(node.textContent);
      if (data.family !== scope.family || !Array.isArray(data.items) || (data.mode != null && data.mode !== scope.mode)) throw new Error('Catalogue family does not match request');
      items.push(...data.items.filter(item => matchesFamily(item, scope.family)));
      if (data.next == null || data.next === false || data.next === '') url = null;
      else if (typeof data.next === 'string') url = nextURL(data.next, url, scope);
      else throw new Error('Invalid catalogue next page');
    }
    if (url) throw new Error('Catalogue pagination incomplete');
    return frozenData({ items, family: scope.family, fetchedAt: Date.now() });
  }
  function get(source, options = {}) {
    let scope;
    try { scope = requestScope(source, options); } catch (error) { return Promise.reject(error); }
    // A force refresh and a second carousel still share an existing request.
    if (inflight.has(scope.key)) return inflight.get(scope.key);
    const wasStale = entries.has(scope.key) && !fresh(entries.get(scope.key));
    prune();
    const cached = entries.get(scope.key);
    if (!options.force && cached) return Promise.resolve(peek(source, options));
    const revalidate = Boolean(options.force || wasStale || staleKeys.has(scope.key));
    const promise = load(scope, revalidate).then(data => {
      remember(scope.key, data);
      return data;
    }).finally(() => inflight.delete(scope.key));
    inflight.set(scope.key, promise);
    return promise;
  }
  // The shared request is deliberately not exposed for per-instance aborts.
  // Consumers ignore obsolete results with their own lifecycle/request token.
  window.CsgCatalogueCache = Object.freeze({ get, peek, ttl });
})();
